import React, { useEffect, useMemo, useState } from "react";
import PageTitle from "../../ui/PageTitle";
import { Button, Col, Container, Dropdown, Form, Row, Spinner, Stack } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/free-solid-svg-icons";
import { Goal } from "../../../types/goal";
import { fetchGoalCategories, fetchGoalTemplates, postGoalCategory } from "../../../services/httpClient";
import useApp from "../../../utils/useApp";
import GoalTemplateCard from "./GoalTemplateCard";
import GoalCategoryCard from "./GoalCategoryCard";
import SearchBar from "../../ui/SearchBar";
import CreateGoalCard from "../../ui/CreateGoalCard";
import SaveChangesModal from "../../ui/SaveChangesModal";
import Input from "../../ui/Input";

// past this many categories the filter buttons collapse into a single dropdown
const MAX_CATEGORY_BUTTONS = 4;

interface OrgTemplateState {
  isActive?: boolean;
  searchRef: any;
  setOrgToSave: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

const OrgGoalTemplates = ({ isActive = true, searchRef, setOrgToSave, setShowToast, setToastText, setToastBody }: OrgTemplateState) => {
  const { auth, showMenu }: any = useApp();
  const [showCreateGoal, setShowCreateGoal] = useState<boolean>(false);
  const [goalSaved, setGoalSaved] = useState<boolean>(false);
  const [goalTemplates, setGoalTemplates] = useState<Goal[]>([]);
  const [goalTemplatesFiltered, setGoalTemplatesFiltered] = useState<Goal[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showCategoryView, setShowCategoryView] = useState<boolean>(false);
  const [showAddCategory, setShowAddCategory] = useState<boolean>(false);
  const [newCategoryName, setNewCategoryName] = useState<string>("");
  const [goalCategories, setGoalCategories] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    getGoalTemplates(true);
    getGoalCategories();
  }, [auth?.profile]);

  // Settings keeps every tab mounted, so SearchBar's mount-time focus never lands; focus the search box whenever this tab is shown
  useEffect(() => {
    if (isActive && !isLoading && !showCreateGoal && !showCategoryView) {
      searchRef?.current?.focus();
    }
  }, [isActive, isLoading, showCreateGoal, showCategoryView]);

  const getGoalTemplates = (loading: boolean) => {
    setIsLoading(loading);

    setErrMsg("");

    fetchGoalTemplates(auth?.profile?.userOrgID)
      .then((res: any) => {
        var temps = res[0].data;
        temps = temps.map((t: any) => {
          return { ...t, key: t.id };
        });
        setGoalTemplates(temps);
        setGoalTemplatesFiltered(temps);
        setIsLoading(false);
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg);
        setIsLoading(false);
      });
  };

  const getGoalCategories = () => {
    fetchGoalCategories()
      .then((res: any) => {
        let cats = res[0].data;
        cats = [{ id: 0, key: "0", name: "None" }, ...cats];
        setGoalCategories(cats);
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg);
      });
  };

  const onClickAddGoalCategory = (newCategoryName: string) => {
    const newCategory = newCategoryName.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());

    return postGoalCategory({ name: newCategory })
      .then((res: any) => {
        setShowToast(true);
        setToastText("Category Created");
        setToastBody(newCategory);
        setNewCategoryName("");
        setShowAddCategory(false);
        getGoalCategories();
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg);
        throw err;
      });
  };

  const onClickSaveSubmitNewCategory = (e: any) => {
    e?.preventDefault();
    onClickAddGoalCategory(newCategoryName);
  };

  const onClickAddCategoryOnly = () => {
    setShowAddCategory(true);
    setErrMsg("");
  };

  const onClickAddCategoryOnlyCancel = () => {
    setNewCategoryName("");
    setShowAddCategory(false);
    setErrMsg("");
  };

  const onUpdateNewCategoryName = (e: any) => {
    setNewCategoryName(e.target.value);
    setErrMsg("");
  };

  const onClickViewCategories = () => {
    setShowCategoryView(true);
    setErrMsg("");
  };

  const onClickBackFromCategories = () => {
    setShowCategoryView(false);
    setSearchText("");
    setGoalTemplatesFiltered(goalTemplates);
    setCategoryFilter("all");
    setShowAddCategory(false);
    setErrMsg("");
  };

  const onClickCreateGoal = () => {
    //Set Create Goal Card to null values
    setGoalSaved(false);
    setShowCreateGoal(true);
  };

  const onClickCreateGoalDone = () => {
    setShowCreateGoal(false);
    setSearchText("");
    setCategoryFilter("all");
    getGoalTemplates(true);
  };

  const onPerformSearchLogic = (text: string) => {
    const lowerText = text.toLowerCase();
    var myFiltered: any;
    var myCases = goalTemplates;

    //filter by name of case
    myFiltered = myCases?.filter((item: any) => {
      return item.name.toLowerCase().match(lowerText);
    });

    setGoalTemplatesFiltered(myFiltered);
  };

  const groupGoals = (goals: Goal[]) => {
    const groups = new Map<string, { key: string; name: string; goals: Goal[] }>();

    goals.forEach((g: Goal) => {
      const key = g.category_id ? String(g.category_id) : "uncategorized";
      const name = g.category_name || "Uncategorized";

      if (!groups.has(key)) {
        groups.set(key, { key, name, goals: [] });
      }
      groups.get(key)!.goals.push(g);
    });

    const sorted = Array.from(groups.values()).sort((a, b) => {
      if (a.key === "uncategorized") return 1;
      if (b.key === "uncategorized") return -1;
      return a.name.localeCompare(b.name);
    });

    sorted.forEach((group) => group.goals.sort((a, b) => a.name.localeCompare(b.name)));

    return sorted;
  };

  // categories offered as filters come from every template, so the buttons stay put while searching
  // like user roles, every real category gets a button even when it has no templates yet
  const allGroups = useMemo(() => {
    const groups = groupGoals(goalTemplates);
    goalCategories
      .filter((c: any) => c.id !== 0 && !groups.some((g) => g.key === String(c.id)))
      .forEach((c: any) => groups.push({ key: String(c.id), name: c.name, goals: [] }));

    return groups.sort((a, b) => {
      if (a.key === "uncategorized") return 1;
      if (b.key === "uncategorized") return -1;
      return a.name.localeCompare(b.name);
    });
  }, [goalTemplates, goalCategories]);
  // fall back to All if the selected category no longer has any templates
  const activeCategory = categoryFilter !== "all" && allGroups.some((g) => g.key === categoryFilter) ? categoryFilter : "all";
  // the Uncategorized bucket is not a real category, so it does not count toward the button limit
  const realCategoryCount = allGroups.filter((g) => g.key !== "uncategorized").length;
  const selectedGroup = allGroups.find((g) => g.key === activeCategory);
  const groupedGoalTemplates = useMemo(
    () => groupGoals(goalTemplatesFiltered).filter((g) => activeCategory === "all" || g.key === activeCategory),
    [goalTemplatesFiltered, activeCategory],
  );

  return (
    <>
      {isLoading ? (
        <Container className={showMenu ? "lg div-main-menu" : "lg div-main"}>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : showCategoryView ? (
        <>
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg}
          </p>
          <PageTitle
            title="Goal Categories"
            type="section"
            buttonTitle="Add Category"
            onButtonClick={onClickAddCategoryOnly}
            buttonDisabled={showAddCategory}
            backButtonText="Back to Goal Templates"
            onBackClick={onClickBackFromCategories}
          />
          <Row className="mb-3"></Row>
          {showAddCategory && (
            <Form onSubmit={(e: any) => onClickSaveSubmitNewCategory(e)}>
              <Row className="mb-3">
                <Col xs={6}>
                  <Input
                    label="Name"
                    type="text"
                    value={newCategoryName}
                    onUpdate={(e: any) => onUpdateNewCategoryName(e)}
                    hasFocus={true}
                  />
                </Col>
                <Col xs={"auto"} className="d-flex align-items-center">
                  <Button type="submit" className="app-secondary-color button me-3" disabled={!newCategoryName}>
                    Add
                  </Button>
                  <div className="vr my-2" />
                  <Button className="app-primary-color button ms-3" onClick={onClickAddCategoryOnlyCancel}>
                    Cancel
                  </Button>
                </Col>
              </Row>
            </Form>
          )}
          <Row>
            <Col xs={6}>
              {goalCategories?.length === 0 ? (
                <p className="mt-2">No Categories</p>
              ) : (
                goalCategories
                  .slice(1)
                  .map((category: any) => (
                    <GoalCategoryCard
                      key={category.id}
                      category={category}
                      getGoalCategories={getGoalCategories}
                      setOrgToSave={setOrgToSave}
                      setShowToast={setShowToast}
                      setToastText={setToastText}
                      setToastBody={setToastBody}
                      setErrMsg={setErrMsg}
                    />
                  ))
              )}
            </Col>
          </Row>
        </>
      ) : (
        <>
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg}
          </p>
          {showCreateGoal ? (
            <PageTitle title="Create Goal Template" type="section" backButtonText="Back to Goal Templates" onBackClick={onClickCreateGoalDone} />
          ) : (
            <Row>
              <Col xs={6}>
                <h4 className="app-header-text">Goal Templates</h4>
              </Col>
              <Col className="text-end">
                <Button className="app-secondary-color button me-3" onClick={onClickViewCategories}>
                  Categories
                </Button>
                <Button className="app-primary-color button" onClick={onClickCreateGoal}>
                  Create Template
                </Button>
              </Col>
            </Row>
          )}
          <Row className="mb-4"></Row>
          {showCreateGoal && (
            <CreateGoalCard
              goalSaved={goalSaved}
              setGoalSaved={setGoalSaved}
              templateMode={true}
              setShowToast={setShowToast}
              setToastText={setToastText}
              setToastBody={setToastBody}
            />
          )}
          {!showCreateGoal && (
            <>
              <Row className="my-3">
                <Col xs={12} className="d-flex justify-content-center">
                  <SearchBar
                    cssClass="search w-100"
                    placeholder="Search Goal Templates"
                    searchText={searchText}
                    setSearchText={setSearchText}
                    searchLogic={onPerformSearchLogic}
                    externalRef={searchRef}
                  />
                </Col>
              </Row>
              {allGroups.length > 0 && (
                <Row className="mb-3">
                  <Col xs={12} className="d-flex flex-wrap justify-content-center gap-3">
                    <Button
                      className={activeCategory === "all" ? "app-primary-color button" : "app-primary-color-inverse button"}
                      onClick={() => setCategoryFilter("all")}
                    >
                      {"All (" + goalTemplates.length + ")"}
                    </Button>
                    {realCategoryCount <= MAX_CATEGORY_BUTTONS ? (
                      allGroups.map((group) => (
                        <Button
                          key={group.key}
                          className={activeCategory === group.key ? "app-primary-color button" : "app-primary-color-inverse button"}
                          onClick={() => setCategoryFilter(group.key)}
                        >
                          {group.name + " (" + group.goals.length + ")"}
                        </Button>
                      ))
                    ) : (
                      <Dropdown>
                        <Dropdown.Toggle
                          id="goal-category-filter"
                          variant="none"
                          className={activeCategory !== "all" ? "app-primary-color button" : "app-primary-color-inverse button"}
                        >
                          {selectedGroup
                            ? selectedGroup.name + " (" + selectedGroup.goals.length + ")"
                            : "Categories (" + allGroups.length + ")"}
                        </Dropdown.Toggle>
                        <Dropdown.Menu style={{ maxHeight: 320, overflowY: "auto" }}>
                          {allGroups.map((group) => (
                            <Dropdown.Item
                              key={group.key}
                              className={activeCategory === group.key ? "app-tertiary-color-inverse" : ""}
                              onClick={() => setCategoryFilter(activeCategory === group.key ? "all" : group.key)}
                            >
                              {group.name + " (" + group.goals.length + ")"}
                              {activeCategory === group.key ? <FontAwesomeIcon icon={faClose} className="ms-2" /> : ""}
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                    )}
                  </Col>
                </Row>
              )}
              <Row className="mt-3">
                <Col xs={12}>
                  {groupedGoalTemplates.length === 0 ? (
                    <div className="mt-2">
                      <p className="mb-1">
                        {selectedGroup && selectedGroup.goals.length === 0
                          ? `No ${selectedGroup.name} Goal Templates`
                          : "No Current Templates"}
                      </p>
                    </div>
                  ) : (
                    <>
                      {groupedGoalTemplates.map((group) => (
                        <div key={group.key} className="mb-4">
                          <h4 className="app-header-text mb-3">{group.name}</h4>
                          <Stack gap={3}>
                            {group.goals.map((g: Goal) => (
                              <GoalTemplateCard
                                key={g.id}
                                goal={g}
                                getGoalTemplates={getGoalTemplates}
                                getGoalCategories={getGoalCategories}
                                setSearchText={setSearchText}
                                categories={goalCategories}
                                setOrgToSave={setOrgToSave}
                                setShowToast={setShowToast}
                                setToastText={setToastText}
                                setToastBody={setToastBody}
                                setErrMsg={setErrMsg}
                              />
                            ))}
                          </Stack>
                        </div>
                      ))}
                    </>
                  )}
                </Col>
              </Row>
              <SaveChangesModal showModal={showModal} setShowModal={setShowModal} path="/settings" />
            </>
          )}
        </>
      )}
    </>
  );
};

export default React.memo(OrgGoalTemplates);
